import { runBatchedOperation } from '@/user_files/batching/run-batched-operation'
import { buildSelectionBatchesFoldersFirstThenFiles, type SelectionBatch } from '@/user_files/batching/selection-batches'
import { UserFilesBatchedOperationKind } from '@/user_files/batching/store/slice'
import { kDeleteMaxFileIdsPerRequest, kDeleteMaxFolderIdsPerRequest } from '@/user_files/delete/constants'
import { BatchedDeleteSelectionError } from '@/user_files/delete/errors'
import { postDeleteSelection } from '@/user_files/delete/fetch/post-delete-selection'
import type { DeleteSelectionRequest, DeleteSelectionResponse } from '@/user_files/delete/schemas'
import { kMoveMaxFileIdsPerRequest, kMoveMaxFolderIdsPerRequest } from '@/user_files/move/constants'
import { BatchedMoveError } from '@/user_files/move/errors'
import { postMoveSelection } from '@/user_files/move/fetch/post-move-selection'
import type { MoveSelectionRequest, MoveSelectionResponse } from '@/user_files/move/schemas'
import type { FileSelectionPrecondition, FolderPathPrecondition, FolderSelectionPrecondition } from '@/user_files/schemas/preconditions'
import { getUserFilesApiErrorDetail } from '@/user_files/utils/api-error'
import { selectPreconditionsById } from '@/user_files/utils/preconditions'
import { ApiError } from '@/util/errors/api-error'

export type ExecuteUserFilesBatchedOperationParams = {
  kind: UserFilesBatchedOperationKind
  destinationFolderId?: string
  fileIds: readonly string[]
  folderIds: readonly string[]
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
  isRootFolderId?: (folderId: string) => boolean
  onProgress?: (processedItems: number) => void
}

export type ExecuteUserFilesBatchedOperationResult =
  | {
      kind: UserFilesBatchedOperationKind.MoveSelection
      aggregate: MoveSelectionResponse
      processedItems: number
    }
  | {
      kind: UserFilesBatchedOperationKind.DeleteSelection
      aggregate: DeleteSelectionResponse
      processedItems: number
    }

export async function executeUserFilesBatchedOperation(params: ExecuteUserFilesBatchedOperationParams): Promise<ExecuteUserFilesBatchedOperationResult> {
  const hasFiles = params.fileIds.length > 0
  const hasFolders = params.folderIds.length > 0
  if (!hasFiles && !hasFolders) {
    throw new Error('Operation requires at least one file or folder')
  }

  if (params.kind === UserFilesBatchedOperationKind.DeleteSelection && params.isRootFolderId) {
    const rootFolderIds = params.folderIds.filter((folderId) => params.isRootFolderId?.(folderId))
    if (rootFolderIds.length > 0) {
      throw new Error('Root folders cannot be deleted. Please refresh and try again.')
    }
  }

  if (params.kind === UserFilesBatchedOperationKind.MoveSelection && !params.destinationFolderId) {
    throw new Error('Move is missing a destination folder')
  }

  const selectionBatches = buildSelectionBatchesFoldersFirstThenFiles({
    fileIds: params.fileIds,
    folderIds: params.folderIds,
    maxFileIdsPerBatch: params.kind === UserFilesBatchedOperationKind.MoveSelection ? kMoveMaxFileIdsPerRequest : kDeleteMaxFileIdsPerRequest,
    maxFolderIdsPerBatch: params.kind === UserFilesBatchedOperationKind.MoveSelection ? kMoveMaxFolderIdsPerRequest : kDeleteMaxFolderIdsPerRequest,
  })

  let processedItems = 0

  if (params.kind === UserFilesBatchedOperationKind.MoveSelection) {
    const destinationFolderId = params.destinationFolderId as string

    const buildMoveRequest = (batch: SelectionBatch): MoveSelectionRequest => {
      const request: MoveSelectionRequest = {
        destination_folder_id: destinationFolderId,
        file_ids: batch.fileIds,
        folder_ids: batch.folderIds,
      }
      const filePreconditions = selectPreconditionsById(params.filePreconditions, batch.fileIds)
      const folderPreconditions = selectPreconditionsById(params.folderPreconditions, batch.folderIds)

      if (filePreconditions) request.file_preconditions = filePreconditions
      if (folderPreconditions) request.folder_preconditions = folderPreconditions
      if (params.destinationFolderPrecondition) request.destination_folder_precondition = params.destinationFolderPrecondition

      return request
    }

    const initialAggregate: MoveSelectionResponse = {
      destination_folder_id: destinationFolderId,
      moved_file_ids: [],
      moved_folder_ids: [],
      unchanged_file_ids: [],
      unchanged_folder_ids: [],
    }

    const aggregate = await runBatchedOperation<SelectionBatch, MoveSelectionResponse, MoveSelectionResponse>({
      batches: selectionBatches,
      initialAggregate,
      runBatch: async (batch) => postMoveSelection(buildMoveRequest(batch)),
      mergeIntoAggregate: (aggregate, result, batch) => {
        aggregate.moved_file_ids.push(...result.moved_file_ids)
        aggregate.moved_folder_ids.push(...result.moved_folder_ids)
        aggregate.unchanged_file_ids.push(...result.unchanged_file_ids)
        aggregate.unchanged_folder_ids.push(...result.unchanged_folder_ids)

        processedItems += batch.fileIds.length + batch.folderIds.length
        params.onProgress?.(processedItems)
      },
      getErrorMessage: (cause) => getUserFilesOperationErrorMessage({ kind: UserFilesBatchedOperationKind.MoveSelection, cause }),
      createError: ({ message, partialResult, failedBatch, cause }) => {
        const failedRequest = buildMoveRequest(failedBatch)

        return new BatchedMoveError({
          message,
          partialResult,
          failedBatch: failedRequest,
          cause,
        })
      },
    })

    return { kind: UserFilesBatchedOperationKind.MoveSelection, aggregate, processedItems }
  }

  const initialAggregate: DeleteSelectionResponse = {
    deleted_file_ids: [],
    deleted_folder_ids: [],
    directly_deleted_file_ids: [],
    directly_deleted_folder_ids: [],
    unchanged_file_ids: [],
    unchanged_folder_ids: [],
  }

  const buildDeleteRequest = (batch: SelectionBatch): DeleteSelectionRequest => {
    const request: DeleteSelectionRequest = {
      file_ids: batch.fileIds,
      folder_ids: batch.folderIds,
    }
    const filePreconditions = selectPreconditionsById(params.filePreconditions, batch.fileIds)
    const folderPreconditions = selectPreconditionsById(params.folderPreconditions, batch.folderIds)

    if (filePreconditions) request.file_preconditions = filePreconditions
    if (folderPreconditions) request.folder_preconditions = folderPreconditions

    return request
  }

  const aggregate = await runBatchedOperation<SelectionBatch, DeleteSelectionResponse, DeleteSelectionResponse>({
    batches: selectionBatches,
    initialAggregate,
    runBatch: async (batch) => postDeleteSelection(buildDeleteRequest(batch)),
    mergeIntoAggregate: (aggregate, result, batch) => {
      aggregate.deleted_file_ids.push(...result.deleted_file_ids)
      aggregate.deleted_folder_ids.push(...result.deleted_folder_ids)
      aggregate.directly_deleted_file_ids.push(...result.directly_deleted_file_ids)
      aggregate.directly_deleted_folder_ids.push(...result.directly_deleted_folder_ids)
      aggregate.unchanged_file_ids.push(...result.unchanged_file_ids)
      aggregate.unchanged_folder_ids.push(...result.unchanged_folder_ids)

      processedItems += batch.fileIds.length + batch.folderIds.length
      params.onProgress?.(processedItems)
    },
    getErrorMessage: (cause) => getUserFilesOperationErrorMessage({ kind: UserFilesBatchedOperationKind.DeleteSelection, cause }),
    createError: ({ message, partialResult, failedBatch, cause }) => {
      const failedRequest = buildDeleteRequest(failedBatch)

      return new BatchedDeleteSelectionError({
        message,
        partialResult,
        failedBatch: failedRequest,
        cause,
      })
    },
  })

  return { kind: UserFilesBatchedOperationKind.DeleteSelection, aggregate, processedItems }
}

export function getUserFilesOperationErrorMessage(params: { kind: UserFilesBatchedOperationKind; cause: unknown }): string {
  const cause = params.cause

  if (cause instanceof ApiError) {
    const detail = getUserFilesApiErrorDetail(cause)
    if (detail?.message) return detail.message

    switch (cause.status) {
      case 413:
        return 'Selection too large. Please reduce the selection and try again.'
      case 409:
        return 'Selection changed. Please refresh and try again.'
      case 404:
        return params.kind === UserFilesBatchedOperationKind.MoveSelection
          ? 'Selection changed. Please refresh and try again.'
          : 'Some items could not be found. Please refresh and try again.'
      case 400:
      case 422:
        return cause.message || 'Invalid selection. Please refresh and try again.'
      default:
        return cause.message || 'Operation failed. Please refresh and try again.'
    }
  }

  if (cause instanceof Error) return cause.message
  return 'Operation failed. Please refresh and try again.'
}
